import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import { lazy, Suspense } from "react";
import { QuoteModal } from "@/components/QuoteModal";
const HeroQuoteForm = lazy(() => import("@/components/HeroQuoteForm"));

export function Hero() {
  const [loadVideo, setLoadVideo] = useState(false);
  const [loadForm, setLoadForm] = useState(false);

  useEffect(() => {
    // Check if device is desktop
    const isDesktop = window.matchMedia("(min-width: 768px)").matches;

    let videoTimer: NodeJS.Timeout;
    let formTimer: NodeJS.Timeout;

    if (isDesktop) {
      // Defer the 4.1MB video until after initial paint to prevent it from blocking Lighthouse payload metrics
      videoTimer = setTimeout(() => setLoadVideo(true), 4500);
    }

    // Defer the heavy form component to avoid forced reflows during initial render
    formTimer = setTimeout(() => setLoadForm(true), 4500);

    // Also load on first interaction
    const handleInteraction = () => {
      if (isDesktop) setLoadVideo(true);
      setLoadForm(true);
    };

    window.addEventListener("scroll", handleInteraction, {
      once: true,
      passive: true,
    });
    window.addEventListener("touchstart", handleInteraction, {
      once: true,
      passive: true,
    });
    window.addEventListener("mousemove", handleInteraction, {
      once: true,
      passive: true,
    });

    return () => {
      if (videoTimer) clearTimeout(videoTimer);
      if (formTimer) clearTimeout(formTimer);
      window.removeEventListener("scroll", handleInteraction);
      window.removeEventListener("touchstart", handleInteraction);
      window.removeEventListener("mousemove", handleInteraction);
    };
  }, []);

  return (
    <div className="relative min-h-[90svh] flex items-center overflow-hidden bg-black">
      {/* Fallback Image
       
  */}
      <picture>
        <source
          media="(max-width: 768px)"
          srcSet="https://vibe.filesafe.space/1779750798598342338/attachments/f0ebb8fb-32ad-4900-85cf-381a9d838097.webp"
        />
        <img
          src="https://i.imgur.com/jnCeynF.webp"
          alt="Luxurious pool and patio"
          className="absolute inset-0 z-0 w-full h-full object-cover"
          {...({ fetchpriority: "high" } as any)}
        />
      </picture>

      {/* Background Video - Deferred for Performance */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        {loadVideo && (
          <video
            src="https://i.imgur.com/4W7w4NT.mp4"
            className="absolute w-full h-full object-cover animate-in fade-in duration-1000"
            autoPlay
            loop
            muted
            playsInline
          />
        )}
      </div>

      {/* Strong Overlay for text readability */}
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-primary/95 via-primary/80 to-black/50" />

      <div className="container relative z-20 mx-auto px-4 pt-36 lg:pt-40 pb-24 lg:pb-32">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8">
          {/* Left Column - Text (60%) */}
          <div className="w-full lg:w-[60%] text-left">
            <div
              className="animate-in fade-in slide-in-from-bottom-5 duration-700"
              style={{ animationDelay: "0ms" }}
            >
              <span className="inline-block py-1 px-3 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium tracking-wider uppercase mb-6 backdrop-blur-xs">
                Premium Hardscaping & Pools
              </span>
            </div>

            <h1
              className="animate-in fade-in slide-in-from-bottom-5 duration-700 text-4xl md:text-5xl lg:text-7xl font-extrabold text-white mb-6 tracking-tight leading-[1.1] [text-shadow:_0_2px_10px_rgba(0,0,0,0.4)]"
              style={{ animationDelay: "100ms" }}
            >
              Transform Your Backyard <br className="hidden md:block" /> Into a{" "}
              <span className="text-secondary">Luxurious Oasis</span>
            </h1>

            <p
              className="animate-in fade-in slide-in-from-bottom-5 duration-700 text-lg md:text-xl text-white/90 mb-8 max-w-2xl font-light [text-shadow:_0_1px_3px_rgba(0,0,0,0.5)]"
              style={{ animationDelay: "200ms" }}
            >
              Family-owned outdoor living experts{" "}
              <span className="relative inline-block whitespace-nowrap pb-2">
                serving all of South Florida since 1996.
                <svg
                  className="absolute left-0 -bottom-1 w-full"
                  viewBox="0 0 300 10"
                  preserveAspectRatio="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <defs>
                    <linearGradient
                      id="underlineGrad"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="0%"
                    >
                      <stop offset="0%" stopColor="#D4A84B" stopOpacity="0.3" />
                      <stop offset="20%" stopColor="#D4A84B" stopOpacity="1" />
                      <stop offset="80%" stopColor="#D4A84B" stopOpacity="1" />
                      <stop
                        offset="100%"
                        stopColor="#D4A84B"
                        stopOpacity="0.3"
                      />
                    </linearGradient>
                  </defs>
                  <path
                    d="M4 6.5 C60 2, 120 8.5, 180 5 S260 2.5, 296 6"
                    fill="none"
                    stroke="url(#underlineGrad)"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M4 8.5 C60 4, 120 10.5, 180 7 S260 4.5, 296 8"
                    fill="none"
                    stroke="#D4A84B"
                    strokeWidth="0.5"
                    strokeLinecap="round"
                    strokeOpacity="0.4"
                  />
                </svg>
              </span>
            </p>

            <div
              className="animate-in fade-in slide-in-from-bottom-5 duration-700 flex flex-col sm:flex-row items-center gap-4"
              style={{ animationDelay: "300ms" }}
            >
              <QuoteModal>
                <Button
                  size="lg"
                  className="h-14 px-8 text-lg bg-accent hover:bg-accent/90 text-accent-foreground w-full sm:w-auto"
                >
                  Get My Free Estimate
                  <ArrowRight className="ml-2 w-5 h-5 hidden lg:block" />
                  <ArrowRight className="ml-2 w-5 h-5 lg:hidden rotate-90" />
                </Button>
              </QuoteModal>
              <Button
                size="lg"
                variant="outline"
                asChild
                className="h-14 px-8 text-lg bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white w-full sm:w-auto backdrop-blur-xs"
              >
                <a
                  href="https://showroom.precisionpaverspools.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit Our Showroom
                  <ExternalLink className="ml-2 w-5 h-5" />
                </a>
              </Button>
            </div>

            <div
              className="animate-in fade-in duration-1000 mt-8 flex flex-wrap gap-6 text-sm font-medium text-white/90"
              style={{ animationDelay: "600ms" }}
            >
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-secondary" />
                <span>Licensed & Insured</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-secondary" />
                <span>30+ Years Serving South Florida</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-secondary" />
                <span>Free Consultations</span>
              </div>
            </div>
          </div>
          {/* Right Column - Form (40%) */}
          <div
            id="quote-form"
            className="animate-in fade-in slide-in-from-right-5 duration-700 w-full lg:w-[40%] max-w-md mx-auto lg:mx-0"
            style={{ animationDelay: "400ms" }}
          >
            {loadForm ? (
              <Suspense
                fallback={
                  <div className="h-[460px] w-full bg-white/5 rounded-3xl animate-pulse" />
                }
              >
                <HeroQuoteForm isTransparent />
              </Suspense>
            ) : (
              <div className="h-[460px] w-full bg-white/5 rounded-3xl animate-pulse" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
